/**
 * The job crew for a ticket, read from IndexedDB when the network cannot say.
 *
 * `api.employee.myJob` is online-only, and a ticket created in a dead zone
 * carries no crew of its own, so the Tech 1-4 picker on FieldTicketsScreen was
 * empty exactly when a hand most needed to fill a ticket in. Two cached
 * sources can answer:
 *
 *   1. The `jobs` store. FieldJobsScreen caches GET /my-jobs rows there, and
 *      that shape carries `crew` built from job_crew_assignments. A sync pull
 *      later overwrites the same row with the raw database row for the job, whose
 *      `crew` jsonb column the API never writes — so the cached record may
 *      honestly say `[]`.
 *   2. Sibling tickets on the same job in the `tickets` store. Since API #518
 *      every field ticket GET carries the job's crew under `crew`, so any
 *      other ticket on the job that was fetched online is a copy of the roster.
 *
 * Returns [] when neither has it. The caller renders the picker with nothing
 * in it — and never a guessed roster.
 */
import { getDB } from './db';
import { crewId, type CrewMemberLike } from '../crew-slot';

const withIds = (rows: unknown): CrewMemberLike[] =>
  Array.isArray(rows) ? rows.filter((c: any) => c && crewId(c)) : [];

export async function readCachedJobCrew(jobId: string): Promise<CrewMemberLike[]> {
  if (!jobId) return [];
  let db;
  try {
    db = await getDB();
  } catch {
    // A sealed or unopenable cache is the empty answer, not an error to show.
    return [];
  }
  try {
    const job = await db.get('jobs', jobId);
    const fromJob = withIds(job?.crew);
    if (fromJob.length > 0) return fromJob;
    const siblings = await db.getAllFromIndex('tickets', 'by-job', jobId);
    for (const t of siblings) {
      const fromTicket = withIds(t?.crew);
      if (fromTicket.length > 0) return fromTicket;
    }
  } catch {
    return [];
  }
  return [];
}
